/* eslint-disable max-len */
import { useMutation } from '@apollo/client';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AdminTable from '../../../components/AdminTable';
import DashboardHeaderWithFilters from '../../../components/DashboardHeaderWithFilters';
import EmptyRecord from '../../../components/EmptyRecord';
import useGetQueryParams from '../../../customHooks/useGetQueryParams';
import useTableColumn from '../../../customHooks/useTableColumn';
import useToast from '../../../customHooks/useToast';
import { GET_ALL_KAABO_COUPONS } from '../../../graphql/admin_queries';
import {
  DEACTIVATE_KAABO_COUPON,
  UPDATE_KAABO_COUPON,
} from '../../../graphql/admin_mutations';
import useCustomQuery from '../../../network/useCustomQuery';
import EmptyMerchantSettlement from '../../../svgs/EmptyMerchantSettlement';
import ListIcon from '../../../svgs/ListIcon';
import EditIcon from '../../../svgs/EditIcon';
import CheckIcon from '../../../svgs/CheckIcon';
import DeactivateMerchantIcon from '../../../svgs/DeactivateMerchantIcon';
import TrashIcon from '../../../svgs/TrashIcon';
import { cleanUpKaaboCoupons } from '../../../utils/helpers';
import { AdminRouteName } from '../../CONSTANTS';
import CreateCouponModal from '../CreateCouponModal';
import EditCouponModal from '../EditCouponModal';
import DeleteCouponModal from '../DeleteCouponModal';
import Styles from '../PartnerCoupons.module.scss';

const limit = 14;

interface IPartnerCouponsRecord {
  isShowCreateModal: boolean;
  setIsShowCreateModal: (v: boolean) => void;
  onCouponsMutated?: () => void;
}

const PartnerCouponsRecord = ({
  isShowCreateModal,
  setIsShowCreateModal,
  onCouponsMutated,
}: IPartnerCouponsRecord) => {
  const Toast = useToast();
  const { push } = useHistory();
  const { getQuery } = useGetQueryParams();
  const search = getQuery('search') || '';
  const page = Number(getQuery('page'));

  const [listRec, setListRec] = useState<any>(null);
  const [editRow, setEditRow] = useState<any>(null);
  const [deleteRow, setDeleteRow] = useState<any>(null);

  const [fetchCoupons, loading, data] = useCustomQuery({
    query: GET_ALL_KAABO_COUPONS,
    onError: () => {},
    onData: () => {},
  });

  const [updateCoupon] = useMutation(UPDATE_KAABO_COUPON);
  const [deactivateCoupon] = useMutation(DEACTIVATE_KAABO_COUPON);

  const refetchList = useCallback(() => {
    if (!page) return undefined;
    return fetchCoupons({
      variables: {
        offset: (page - 1) * limit,
        limit,
        search: search || null,
        status: null,
        partnerId: null,
      },
    });
  }, [page, search, fetchCoupons]);

  const refetchListAndStats = useCallback(() => {
    const p = refetchList();
    if (p && typeof (p as Promise<unknown>).then === 'function') {
      (p as Promise<unknown>)
        .then(() => onCouponsMutated?.())
        .catch(() => {});
    } else {
      onCouponsMutated?.();
    }
  }, [refetchList, onCouponsMutated]);

  const setPage = (pageNum: number) => {
    const sp = new URLSearchParams(window.location.search);
    sp.set('page', String(pageNum));
    push({
      pathname: AdminRouteName.rewardPartnerCoupons,
      search: sp.toString(),
    });
  };

  if (!page) {
    push({ pathname: AdminRouteName.rewardPartnerCoupons, search: 'page=1' });
  }

  useEffect(() => {
    refetchList();
  }, [refetchList]);

  useEffect(() => {
    if (data?.getAllCoupons?.data) {
      setListRec(data.getAllCoupons.data);
    }
  }, [data]);

  const allColumns = [
    'couponCode',
    'couponAlias',
    'partnerLabel',
    'discountLabel',
    'expiryLabel',
    'usesLabel',
    'statusDisplay',
  ];
  const columnLabelFormat: Record<string, string> = {
    couponCode: 'Code',
    couponAlias: 'Tag',
    partnerLabel: 'Partner',
    discountLabel: 'Discount',
    expiryLabel: 'Expires',
    usesLabel: 'Uses',
    statusDisplay: 'Status',
  };
  const { getColumns } = useTableColumn(allColumns, 'reward');

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sp = new URLSearchParams(window.location.search);
    sp.set('page', '1');
    sp.set('search', e.target.value);
    push({
      pathname: AdminRouteName.rewardPartnerCoupons,
      search: sp.toString(),
    });
  };

  const handleActivate = async (row: any) => {
    if (row.status === 'active') {
      Toast.error('Coupon is already active.');
      return;
    }
    if (row.isExpired) {
      Toast.error('Extend expiry before activating.');
      return;
    }
    try {
      const res = await updateCoupon({
        variables: { couponId: row.id, input: { status: 'active' } },
      });
      if (res.data?.updateCoupon?.status === 200) {
        Toast.success('Coupon activated');
        refetchListAndStats();
      } else {
        Toast.error(res.data?.updateCoupon?.message || 'Failed');
      }
    } catch (e: any) {
      Toast.error(e?.graphQLErrors?.[0]?.message || 'Error');
    }
  };

  const handleDeactivate = async (row: any) => {
    if (row.status !== 'active') {
      Toast.error('Only active coupons can be deactivated.');
      return;
    }
    try {
      const res = await deactivateCoupon({ variables: { couponId: row.id } });
      if (res.data?.deactivateCoupon?.status === 200) {
        Toast.success('Coupon deactivated');
        refetchListAndStats();
      } else {
        Toast.error(res.data?.deactivateCoupon?.message || 'Failed');
      }
    } catch (e: any) {
      Toast.error(e?.graphQLErrors?.[0]?.message || 'Error');
    }
  };

  const handleSelectDropdown = (title: string, row: any) => {
    if (title === 'Edit') {
      setEditRow(row);
      return;
    }
    if (title === 'Activate') {
      handleActivate(row);
      return;
    }
    if (title === 'Deactivate') {
      handleDeactivate(row);
      return;
    }
    if (title === 'Delete permanently') {
      setDeleteRow(row);
    }
  };

  const rows = cleanUpKaaboCoupons(listRec?.coupons || []);

  const dropdownData = [
    {
      title: 'Edit',
      color: '#05603A',
      icon: <EditIcon width="14" height="14" color="#05603A" />,
    },
    {
      title: 'Delete permanently',
      color: '#B42318',
      icon: (
        <span className={Styles.dropdownTrashIcon}>
          <TrashIcon />
        </span>
      ),
    },
  ];

  const dropdownActiveDetails = {
    name: 'Deactivate',
    color: 'rgba(255, 89, 89, 1)',
    icon: <DeactivateMerchantIcon />,
  };
  const dropdownDeActiveDetails = {
    name: 'Activate',
    color: 'rgba(71, 84, 103, 1)',
    icon: (
      <CheckIcon width="12px" height="12px" color="rgba(0, 138, 0, 1)" />
    ),
  };

  return (
    <div className={Styles.recordSection}>
      <div className={Styles.recordFilterHeader}>
        <DashboardHeaderWithFilters
          title="All coupons"
          hideFilter
          showSearch
          search={search}
          onSearchChange={onSearchChange}
          searchPlaceholder="Code, tag, or partner name"
        />
      </div>

      <div className="transaction-table-details-cover">
        {listRec?.count ? (
          <AdminTable
            refreshing={loading}
            page={page}
            setPage={setPage}
            pageSize={limit}
            minHeight="calc(100vh - 280px)"
            count={listRec.count}
            contents={rows}
            reference="id"
            hideDominantImage
            allowKeyArr={getColumns(true)}
            mobileAllowKeyArr={[
              'statusDisplay',
              ['couponCode', 'discountLabel'],
              ['expiryLabel', 'partnerLabel'],
            ]}
            handleSelectDropdown={handleSelectDropdown}
            formatLabels={columnLabelFormat}
            actionList
            hasActiveAndInactiveStatus
            dropdownActiveDetails={dropdownActiveDetails}
            dropdownDeActiveDetails={dropdownDeActiveDetails}
            dropdownData={dropdownData}
            dropDownIcon={(
              <div className={Styles.wrapper__icon}>
                <ListIcon />
              </div>
            )}
            dropdownWidth="168px"
          />
        ) : (
          <EmptyRecord
            height="calc(100vh - 240px)"
            title={loading ? 'Loading coupons…' : 'No coupons yet'}
            main="Create a coupon to offer partner-linked or platform-wide discounts on Kaabo."
            loading={loading}
            Icon={EmptyMerchantSettlement}
          />
        )}
      </div>

      {isShowCreateModal && (
        <CreateCouponModal
          closeModal={() => setIsShowCreateModal(false)}
          refetch={refetchListAndStats}
        />
      )}
      {editRow && (
        <EditCouponModal
          coupon={editRow}
          closeModal={() => setEditRow(null)}
          refetch={refetchListAndStats}
        />
      )}
      {deleteRow && (
        <DeleteCouponModal
          coupon={deleteRow}
          closeModal={() => setDeleteRow(null)}
          refetch={refetchListAndStats}
        />
      )}
    </div>
  );
};

export default PartnerCouponsRecord;
